import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import { ROUTES } from '../constants/routes';
import useBedCategoryStore from '../stores/bedCategoryStore';
import useDepartmentStore from '../stores/departmentStore';
import AdminPageHeader from '../components/common/AdminPageHeader';
import FeesBillingTabs from '../components/settings/FeesBillingTabs';
import { ConfirmationModal } from '../components/common';
import {
  BILLING_UNITS,
  BILLING_UNIT_SUFFIX,
  chargeLabels,
  CARE_LEVELS,
  TIERS,
  GST_RATES,
  suggestGstRate,
} from '../constants/bedCategory';
import { getTodayIST } from '../utils/dateIST';
import { ORG_WIDE_FILTER, filterByDeptMembership } from '../utils/deptMembershipFilter';
import './ProcedureCatalogPage.css';

// B2 / S1–S3 — Bed Category catalog (IPD tariff masters). Clone of the
// LabCatalogPage/ProcedureCatalogPage admin-catalog pattern MINUS the A4
// department machinery: the catalog is ORG-GLOBAL (no "Visible to" field).
// Rates are effective-dated + non-retroactive (S2): the server keeps an
// append-only rate_history; this form only picks the effective-from date of
// the NEW full tariff (room + nursing + RMO versioned together).

const blankRow = () => ({
  name: '',
  billing_unit: 'PER_DAY',
  per_day_charge: '',
  nursing_charge_per_day: '',
  rmo_charge_per_day: '',
  rate_effective_from: getTodayIST(),
  gst_rate: 0,
  ac: false,
  sharing: '',
  tier: '',
  care_level: '',
  amenities: '',       // comma-separated in the form; list[str] on the wire
  display_order: '',
  color: '',
  active: true,
});

function validate(row, todayIST) {
  const e = {};
  if (!row.name || !row.name.trim()) e.name = 'Required';
  if (row.per_day_charge === '' || row.per_day_charge === null || row.per_day_charge === undefined) {
    e.per_day_charge = 'Required';
  } else if (Number.isNaN(Number(row.per_day_charge)) || Number(row.per_day_charge) < 0) {
    e.per_day_charge = 'Must be 0 or more';
  }
  for (const k of ['nursing_charge_per_day', 'rmo_charge_per_day']) {
    if (row[k] !== '' && (Number.isNaN(Number(row[k])) || Number(row[k]) < 0)) {
      e[k] = 'Must be 0 or more';
    }
  }
  if (row.sharing !== '' && (!Number.isInteger(Number(row.sharing)) || Number(row.sharing) < 1)) {
    e.sharing = 'Must be a whole number ≥ 1';
  }
  if (row.rate_effective_from && row.rate_effective_from < todayIST) {
    e.rate_effective_from = 'Non-retroactive — pick today or a later date';
  }
  return e;
}

/** Parse the comma-separated amenities input → clean list[str]. */
function parseAmenities(text) {
  return String(text || '')
    .split(',')
    .map((a) => a.trim())
    .filter(Boolean);
}

export default function BedCatalogPage() {
  const { isDark } = useTheme();
  const { hasPermission } = usePermissions();

  const {
    bedCategories,
    isLoading,
    error,
    includeInactive,
    fetchBedCategories,
    createBedCategory,
    updateBedCategory,
    deactivateBedCategory,
    setIncludeInactive,
    setBedCategoryDepartments,
    clearError,
  } = useBedCategoryStore();

  // B5 / S2 — org departments for the "Visible to" tag field (clone of A4/A5).
  // Only ACTIVE departments are offered (a soft-deleted dept drops out; an
  // existing dead tag is intersected out server-side → not shown).
  const departments = useDepartmentStore((s) => s.departments);
  const fetchDepartments = useDepartmentStore((s) => s.fetchDepartments);
  const activeDepts = useMemo(
    () => (departments || []).filter((d) => d.active !== false)
      .map((d) => ({ dept_id: d.dept_id || d.id, name: d.name })),
    [departments],
  );
  const deptNameById = useMemo(() => {
    const m = {};
    for (const d of activeDepts) m[d.dept_id] = d.name;
    return m;
  }, [activeDepts]);

  const [form, setForm] = useState(null); // null | {mode:'add'} | {mode:'edit', id}
  const [row, setRow] = useState(blankRow());
  const [orig, setOrig] = useState(null); // the server row being edited (diff base)
  const [gstTouched, setGstTouched] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [banner, setBanner] = useState(null);
  // B5 — dept-tag picker selection for the current form, seeded on edit; saved
  // via the PUT as part of the SAME Save click. `deptFilter` is the display-only
  // list view-filter (union: tagged-to-selected ∪ ALL untagged/org-wide).
  const [deptIds, setDeptIds] = useState([]);
  const [deptFilter, setDeptFilter] = useState('');

  const todayIST = getTodayIST();

  // Initial load — stable deps (zustand action identity), single-flight stores.
  useEffect(() => {
    fetchBedCategories();
    fetchDepartments();
  }, [fetchBedCategories, fetchDepartments]);

  // ── Cross-field: billing_unit → charge labels (both directions) ─────
  const labels = chargeLabels(row.billing_unit);

  // ── Cross-field: care_level + per_day_charge → suggested GST ───────
  const suggested = suggestGstRate(row.care_level, row.per_day_charge);
  const showResuggest = !!form && gstTouched && Number(row.gst_rate) !== suggested;

  const change = (k, v) => {
    if (k === 'gst_rate') {
      // Manual override — stop auto-following; a later driver change shows a
      // re-suggest cue instead of clobbering (S3 Screen rules).
      setGstTouched(true);
      setRow((r) => ({ ...r, gst_rate: v }));
      return;
    }
    setRow((r) => {
      const next = { ...r, [k]: v };
      if ((k === 'care_level' || k === 'per_day_charge') && !gstTouched) {
        next.gst_rate = suggestGstRate(next.care_level, next.per_day_charge);
      }
      return next;
    });
  };

  const toggleDept = (deptId) => {
    setDeptIds((prev) =>
      prev.includes(deptId) ? prev.filter((d) => d !== deptId) : [...prev, deptId]
    );
  };

  const openAdd = () => {
    setRow(blankRow());
    setOrig(null);
    setGstTouched(false);
    setErrors({});
    setBanner(null);
    setDeptIds([]);
    setForm({ mode: 'add' });
  };

  const openEdit = (p) => {
    // Seed ONLY form fields from the server row — read-only/derived fields
    // (rate_history, is_preset, organization_id, timestamps) never enter the
    // form state, so they can never leak into a write body (extra=forbid).
    setRow({
      name: p.name || '',
      billing_unit: p.billing_unit || 'PER_DAY',
      per_day_charge: p.per_day_charge ?? '',
      nursing_charge_per_day: p.nursing_charge_per_day ?? '',
      rmo_charge_per_day: p.rmo_charge_per_day ?? '',
      rate_effective_from: getTodayIST(), // effective-from of the NEW tariff entry
      gst_rate: p.gst_rate ?? 0,
      ac: !!p.ac,
      sharing: p.sharing ?? '',
      tier: p.tier || '',
      care_level: p.care_level || '',
      amenities: (p.amenities || []).join(', '),
      display_order: p.display_order ?? '',
      color: p.color || '',
      active: p.active !== false,
    });
    setOrig(p);
    setGstTouched(true); // stored rate is authoritative — never auto-clobber on edit
    setErrors({});
    setBanner(null);
    // Seed the dept-tag editor from the row's current (active) tags.
    setDeptIds(Array.isArray(p.department_ids) ? [...p.department_ids] : []);
    setForm({ mode: 'edit', id: p.bed_category_id });
  };

  const close = () => {
    setForm(null);
    setOrig(null);
    setErrors({});
    setDeptIds([]);
  };

  // ── B5 (bug 3) — display-only list view-filter, MEMBERSHIP semantic. Empty
  // ("All departments") = full list; a dept anchor shows ONLY rows tagged to it
  // (org-wide/untagged excluded); the explicit "Org-wide" option shows ONLY
  // untagged rows. Distinct from the VISIBILITY union used by consumption
  // pickers — this "what's attached" view is management-only. ────────────
  const visibleCategories = useMemo(
    () => filterByDeptMembership(bedCategories, deptFilter),
    [bedCategories, deptFilter],
  );

  // ── Edit view of the append-only rate history (S2) ─────────────────
  const rateHistory = useMemo(
    () => (Array.isArray(orig?.rate_history) ? orig.rate_history : []),
    [orig],
  );
  const currentEntry = useMemo(() => {
    const past = rateHistory.filter((h) => h.from_date <= todayIST);
    return past.length ? past[past.length - 1] : rateHistory[0] || null;
  }, [rateHistory, todayIST]);
  const scheduledEntries = useMemo(
    () => rateHistory.filter((h) => h.from_date > todayIST),
    [rateHistory, todayIST],
  );

  // ── Save ────────────────────────────────────────────────────────────
  const submit = async () => {
    const e = validate(row, todayIST);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);

    let result;
    if (form.mode === 'add') {
      const body = {
        name: row.name.trim(),
        billing_unit: row.billing_unit,
        per_day_charge: Number(row.per_day_charge),
        gst_rate: Number(row.gst_rate),
        rate_effective_from: row.rate_effective_from || todayIST,
      };
      if (row.nursing_charge_per_day !== '') body.nursing_charge_per_day = Number(row.nursing_charge_per_day);
      if (row.rmo_charge_per_day !== '') body.rmo_charge_per_day = Number(row.rmo_charge_per_day);
      if (row.ac) body.ac = true;
      if (row.sharing !== '') body.sharing = Number(row.sharing);
      if (row.tier) body.tier = row.tier;
      if (row.care_level) body.care_level = row.care_level;
      const amenities = parseAmenities(row.amenities);
      if (amenities.length) body.amenities = amenities;
      if (row.display_order !== '') body.display_order = Number(row.display_order);
      if (row.color.trim()) body.color = row.color.trim();
      result = await createBedCategory(body);
    } else {
      // PATCH = allowlist DIFF vs the original row. Charge fields are sent
      // only when actually changed, so a name-only edit never appends a
      // rate-history entry server-side; read-only fields can never leak.
      const patch = {};
      const changed = (key, newVal, origVal) => {
        if (JSON.stringify(newVal) !== JSON.stringify(origVal)) patch[key] = newVal;
      };
      changed('name', row.name.trim(), orig.name);
      changed('billing_unit', row.billing_unit, orig.billing_unit || 'PER_DAY');
      changed('per_day_charge', Number(row.per_day_charge), Number(orig.per_day_charge));
      changed(
        'nursing_charge_per_day',
        row.nursing_charge_per_day === '' ? null : Number(row.nursing_charge_per_day),
        orig.nursing_charge_per_day ?? null,
      );
      changed(
        'rmo_charge_per_day',
        row.rmo_charge_per_day === '' ? null : Number(row.rmo_charge_per_day),
        orig.rmo_charge_per_day ?? null,
      );
      changed('gst_rate', Number(row.gst_rate), Number(orig.gst_rate ?? 0));
      changed('ac', !!row.ac, !!orig.ac);
      changed('sharing', row.sharing === '' ? null : Number(row.sharing), orig.sharing ?? null);
      changed('tier', row.tier || null, orig.tier || null);
      changed('care_level', row.care_level || null, orig.care_level || null);
      changed('amenities', parseAmenities(row.amenities), orig.amenities || []);
      changed('active', row.active !== false, orig.active !== false);
      changed('display_order', row.display_order === '' ? null : Number(row.display_order), orig.display_order ?? null);
      changed('color', row.color.trim() || null, orig.color || null);

      const rateChanged = ['per_day_charge', 'nursing_charge_per_day', 'rmo_charge_per_day']
        .some((k) => k in patch);
      if (rateChanged) {
        // Dates the NEW full-tariff entry (append-only, non-retroactive).
        patch.rate_effective_from = row.rate_effective_from || todayIST;
      }
      // B5 — a dept-tag-only edit must still save (the tags travel via the
      // separate PUT, not the patch). Only "nothing changed at all" is a no-op.
      const seededDepts = Array.isArray(orig.department_ids) ? orig.department_ids : [];
      const deptChanged =
        deptIds.length !== seededDepts.length || deptIds.some((d) => !seededDepts.includes(d));
      if (Object.keys(patch).length === 0 && !deptChanged) {
        setBusy(false);
        setBanner({ kind: 'err', msg: 'No changes to save' });
        return;
      }
      result = Object.keys(patch).length
        ? await updateBedCategory(form.id, patch)
        : { success: true, data: {} }; // dept-tag-only change → skip the PATCH
    }

    if (!result.success) {
      setBusy(false);
      setBanner({ kind: 'err', msg: result.error || 'Save failed' });
      return;
    }
    // B5 — tag departments as part of the SAME Save click, AFTER create/PATCH.
    // Create: only when ≥1 selected (uses the server-assigned id). Edit: always,
    // so a cleared selection reverts the row to org-wide (server RC-3 flip).
    const isAdd = form.mode === 'add';
    const categoryId = isAdd ? result.data?.bed_category_id : form.id;
    if (!isAdd || deptIds.length > 0) {
      if (!categoryId) {
        setBusy(false);
        setBanner({ kind: 'err', msg: 'Bed category created, but departments could not be saved — edit it to set departments.' });
        close();
        return;
      }
      const deptResult = await setBedCategoryDepartments(categoryId, deptIds);
      setBusy(false);
      if (!deptResult.success) {
        setBanner({ kind: 'err', msg: `Bed category ${isAdd ? 'created' : 'updated'}, but departments were not saved: ${deptResult.error || 'unknown error'}` });
        return;
      }
    } else {
      setBusy(false);
    }
    close();
    setBanner({ kind: 'ok', msg: isAdd ? 'Bed category created' : 'Bed category updated' });
  };

  const askDeactivate = (p) => {
    setConfirm({
      title: 'Deactivate bed category?',
      message: `"${p.name}" will be hidden from new IPD use. Existing references keep working. Re-activate later by editing and toggling Active.`,
      onConfirm: async () => {
        setConfirm(null);
        const r = await deactivateBedCategory(p.bed_category_id);
        setBanner(
          r.success
            ? { kind: 'ok', msg: 'Bed category deactivated' }
            : { kind: 'err', msg: r.error || 'Deactivate failed' }
        );
      },
    });
  };

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  const inputCls =
    'w-full rounded-lg px-3 py-2 text-sm border bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100';
  const labelCls =
    'block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1';

  // B6 / S4 (Variant A) — the add/edit form panel. Rendered at the TOP for a new
  // item ({mode:'add'}) and INLINE below its source row for an edit ({mode:'edit'}).
  // One element, placed in exactly one position per render (add XOR edit).
  const formPanel = form && (
    <div className="pcp-add-wrap">
      <div className="pcp-add-title">
        {form.mode === 'add' ? 'New Bed Category' : `Edit · ${orig?.name || ''}`}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <label className={labelCls}>Name *</label>
          <input
            className={inputCls}
            aria-label="Name *"
            value={row.name}
            onChange={(e) => change('name', e.target.value)}
            placeholder="General Ward"
          />
          {errors.name && <div className="text-xs text-rose-600 mt-1">{errors.name}</div>}
        </div>

        <div>
          <label className={labelCls}>Billing unit</label>
          <select
            className={inputCls}
            aria-label="Billing unit"
            value={row.billing_unit}
            onChange={(e) => change('billing_unit', e.target.value)}
          >
            {BILLING_UNITS.map((u) => (
              <option key={u.value} value={u.value}>{u.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>{labels.per_day_charge} (₹) *</label>
          <input
            className={inputCls}
            type="number"
            min="0"
            aria-label={labels.per_day_charge}
            value={row.per_day_charge}
            onChange={(e) => change('per_day_charge', e.target.value)}
          />
          {errors.per_day_charge && (
            <div className="text-xs text-rose-600 mt-1">{errors.per_day_charge}</div>
          )}
        </div>

        <div>
          <label className={labelCls}>{labels.nursing_charge_per_day} (₹)</label>
          <input
            className={inputCls}
            type="number"
            min="0"
            aria-label={labels.nursing_charge_per_day}
            value={row.nursing_charge_per_day}
            onChange={(e) => change('nursing_charge_per_day', e.target.value)}
          />
          {errors.nursing_charge_per_day && (
            <div className="text-xs text-rose-600 mt-1">{errors.nursing_charge_per_day}</div>
          )}
        </div>

        <div>
          <label className={labelCls}>{labels.rmo_charge_per_day} (₹)</label>
          <input
            className={inputCls}
            type="number"
            min="0"
            aria-label={labels.rmo_charge_per_day}
            value={row.rmo_charge_per_day}
            onChange={(e) => change('rmo_charge_per_day', e.target.value)}
          />
          {errors.rmo_charge_per_day && (
            <div className="text-xs text-rose-600 mt-1">{errors.rmo_charge_per_day}</div>
          )}
        </div>

        <div>
          <label className={labelCls}>
            {form.mode === 'add' ? 'Rates effective from' : 'New rates effective from'}
          </label>
          <input
            className={inputCls}
            type="date"
            min={todayIST}
            aria-label="Rates effective from"
            value={row.rate_effective_from}
            onChange={(e) => change('rate_effective_from', e.target.value)}
          />
          {errors.rate_effective_from ? (
            <div className="text-xs text-rose-600 mt-1">{errors.rate_effective_from}</div>
          ) : (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              {form.mode === 'add'
                ? 'Non-retroactive — today or later.'
                : 'Applies when you change a charge — earlier days keep the old tariff.'}
            </div>
          )}
        </div>

        {form.mode === 'edit' && currentEntry && (
          <div className="lg:col-span-3 text-xs rounded-lg px-3 py-2 bg-indigo-50 dark:bg-slate-800 border border-indigo-300 dark:border-indigo-700 text-slate-700 dark:text-slate-200">
            <span className="font-semibold">
              Current tariff (from {currentEntry.from_date}):
            </span>{' '}
            ₹{Number(currentEntry.per_day)} / {BILLING_UNIT_SUFFIX[row.billing_unit] || 'day'}
            {currentEntry.nursing != null && ` · Nursing ₹${Number(currentEntry.nursing)}`}
            {currentEntry.rmo != null && ` · RMO ₹${Number(currentEntry.rmo)}`}
            {scheduledEntries.map((s) => (
              <span key={s.from_date}>
                {' '}· <span className="font-semibold">New tariff from {s.from_date}:</span>{' '}
                ₹{Number(s.per_day)}
                {s.nursing != null && ` · Nursing ₹${Number(s.nursing)}`}
                {s.rmo != null && ` · RMO ₹${Number(s.rmo)}`}
              </span>
            ))}
          </div>
        )}

        <div>
          <label className={labelCls}>Care level</label>
          <select
            className={inputCls}
            aria-label="Care level"
            value={row.care_level}
            onChange={(e) => change('care_level', e.target.value)}
          >
            <option value="">—</option>
            {CARE_LEVELS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>GST</label>
          <select
            className={inputCls}
            aria-label="GST"
            value={row.gst_rate}
            onChange={(e) => change('gst_rate', e.target.value)}
          >
            {GST_RATES.map((g) => (
              <option key={g} value={g}>{g}%</option>
            ))}
          </select>
          {showResuggest ? (
            <div className="text-xs text-amber-700 dark:text-amber-300 mt-1">
              Suggested: {suggested}% (from care level + room rent){' '}
              <button
                type="button"
                className="underline font-semibold"
                onClick={() => setRow((r) => ({ ...r, gst_rate: suggested }))}
              >
                Apply
              </button>
            </div>
          ) : (
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Suggested from care level + room rent — edit if needed.
            </div>
          )}
        </div>

        <div>
          <label className={labelCls}>Tier</label>
          <select
            className={inputCls}
            aria-label="Tier"
            value={row.tier}
            onChange={(e) => change('tier', e.target.value)}
          >
            <option value="">—</option>
            {TIERS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Sharing (beds / room)</label>
          <input
            className={inputCls}
            type="number"
            min="1"
            aria-label="Sharing"
            value={row.sharing}
            onChange={(e) => change('sharing', e.target.value)}
          />
          {errors.sharing && <div className="text-xs text-rose-600 mt-1">{errors.sharing}</div>}
        </div>

        <div>
          <label className={labelCls}>Amenities (comma-separated)</label>
          <input
            className={inputCls}
            aria-label="Amenities"
            value={row.amenities}
            onChange={(e) => change('amenities', e.target.value)}
            placeholder="TV, Attendant couch"
          />
        </div>

        <div>
          <label className={labelCls}>Display order</label>
          <input
            className={inputCls}
            type="number"
            aria-label="Display order"
            value={row.display_order}
            onChange={(e) => change('display_order', e.target.value)}
          />
        </div>

        <div>
          <label className={labelCls}>Colour</label>
          <input
            className={inputCls}
            aria-label="Colour"
            value={row.color}
            onChange={(e) => change('color', e.target.value)}
            placeholder="#0ea5e9"
          />
        </div>

        <div className="flex items-center gap-4 mt-5">
          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
            <input
              type="checkbox"
              checked={row.ac}
              onChange={(e) => change('ac', e.target.checked)}
            />
            AC
          </label>
          {form.mode === 'edit' && (
            <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
              <input
                type="checkbox"
                checked={row.active !== false}
                onChange={(e) => change('active', e.target.checked)}
              />
              Active
            </label>
          )}
        </div>
      </div>

      {/* B5 / S2 — "Visible to" departments (optional, multi). Hidden when
          the org has no active departments (dark-org precedent). Empty
          selection = org-wide. Saved via the PUT in the same Save click.
          Complementary to a ward's physical department — no coupling. */}
      {activeDepts.length > 0 && (
        <div className="mt-3">
          <label className={labelCls}>
            Visible to{' '}
            <span className="text-xs font-normal normal-case text-slate-400 dark:text-slate-500">
              (optional, multi — leave empty for org-wide)
            </span>
          </label>
          <div className="flex flex-wrap gap-4">
            {activeDepts.map((d) => (
              <label key={d.dept_id} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={deptIds.includes(d.dept_id)}
                  onChange={() => toggleDept(d.dept_id)}
                />
                {d.name}
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-2 mt-4">
        <button className="pcp-btn pcp-btn-primary" onClick={submit} disabled={busy}>
          {busy ? 'Saving…' : form.mode === 'add' ? 'Create' : 'Save Changes'}
        </button>
        <button className="pcp-btn pcp-btn-secondary" onClick={close} disabled={busy}>
          Cancel
        </button>
      </div>
    </div>
  );

  // Permission gate — all hooks above; safe to early-return now. Redirect home
  // (not /access-denied) like LabCatalogPage: a missing feature permission in
  // an authenticated session shouldn't push toward logout. The server still
  // 403s every write (defence-in-depth, S1 AC-NEG).
  if (!hasPermission('MANAGE_BED_CONFIG')) {
    return <Navigate to={ROUTES.ORG} replace />;
  }

  return (
    <div className="pcp-page" style={{ background: bg }}>
      <AdminPageHeader />

      <main className="pcp-main">
        <div className="pcp-title-row">
          <div>
            <h1 className="pcp-page-title" style={{ color: isDark ? '#f5f5f7' : '#111827' }}>
              🛏 Bed Categories
            </h1>
            <p className="pcp-page-subtitle" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
              Organisation-wide bed types &amp; tariffs used by IPD admissions and billing.
            </p>
          </div>
          <button className="pcp-btn pcp-btn-primary" onClick={openAdd} disabled={!!form}>
            + Add Bed Category
          </button>
        </div>
        <FeesBillingTabs active="bed-categories" />

        {banner && (
          <div className={`pcp-banner ${banner.kind === 'ok' ? 'pcp-banner-ok' : 'pcp-banner-err'}`}>
            <span>{banner.msg}</span>
            <button onClick={() => setBanner(null)} aria-label="Dismiss">✕</button>
          </div>
        )}

        <div className="pcp-filter-bar">
          <label className="pcp-checkbox">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            Show deactivated
          </label>
          {activeDepts.length > 0 && (
            <select
              className={inputCls}
              style={{ width: 'auto' }}
              aria-label="Filter by department"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
            >
              <option value="">All departments</option>
              <option value={ORG_WIDE_FILTER}>Org-wide</option>
              {activeDepts.map((d) => (
                <option key={d.dept_id} value={d.dept_id}>{d.name}</option>
              ))}
            </select>
          )}
          <span className="pcp-count">{visibleCategories.length} categories</span>
        </div>

        {form?.mode === 'add' && formPanel}

        {isLoading ? (
          <div className="pcp-state">Loading…</div>
        ) : error ? (
          <div className="pcp-state pcp-state-err">
            {error}
            <button
              className="pcp-btn pcp-btn-secondary"
              onClick={() => { clearError(); fetchBedCategories({ force: true }); }}
            >
              Retry
            </button>
          </div>
        ) : bedCategories.length === 0 ? (
          <div className="pcp-state">
            🛏 No bed categories yet. Click + Add Bed Category to create the first one.
          </div>
        ) : (
          <table className="pcp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Billing unit</th>
                <th>Charges</th>
                <th>GST</th>
                <th>Tags</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleCategories.map((p) => {
                const unit = BILLING_UNIT_SUFFIX[p.billing_unit] || 'day';
                const isPlaceholderRate = p.is_preset && (p.rate_history || []).length === 1;
                const isEditing = form?.mode === 'edit' && form.id === p.bed_category_id;
                return (
                  <React.Fragment key={p.bed_category_id}>
                  <tr
                    className={`${p.active === false ? 'pcp-row pcp-row-inactive' : 'pcp-row'}${isEditing ? ' pcp-row-editing' : ''}`}
                  >
                    <td>
                      <div className="pcp-name">{p.name}</div>
                      <div className="pcp-subname">{p.bed_category_id}</div>
                    </td>
                    <td>{(BILLING_UNITS.find((u) => u.value === p.billing_unit) || BILLING_UNITS[0]).label}</td>
                    <td className="pcp-cost">
                      ₹{Number(p.per_day_charge || 0).toFixed(2)} / {unit}
                      {(p.nursing_charge_per_day != null || p.rmo_charge_per_day != null) && (
                        <div className="pcp-subname">
                          {p.nursing_charge_per_day != null && `Nursing ₹${Number(p.nursing_charge_per_day)}`}
                          {p.nursing_charge_per_day != null && p.rmo_charge_per_day != null && ' · '}
                          {p.rmo_charge_per_day != null && `RMO ₹${Number(p.rmo_charge_per_day)}`}
                        </div>
                      )}
                    </td>
                    <td>{p.gst_rate ?? 0}%</td>
                    <td>
                      <div className="pcp-chips">
                        {p.care_level && <span className="pcp-chip">{p.care_level}</span>}
                        {p.tier && <span className="pcp-chip">{p.tier}</span>}
                        {p.ac && <span className="pcp-chip">AC</span>}
                        {p.sharing != null && <span className="pcp-chip">{p.sharing}-bed</span>}
                        {(p.amenities || []).map((a) => (
                          <span key={a} className="pcp-chip">{a}</span>
                        ))}
                        {p.is_preset && <span className="pcp-chip">Preset</span>}
                        {isPlaceholderRate && (
                          <span
                            className="pcp-chip"
                            style={{
                              background: isDark ? '#78350f' : '#fef3c7',
                              color: isDark ? '#fde68a' : '#92400e',
                              borderColor: isDark ? '#b45309' : '#f59e0b',
                            }}
                          >
                            Placeholder rate — edit before billing
                          </span>
                        )}
                        {/* B5 — dept tags (active edges). >0 → "Visible to" chips;
                            [] on an org WITH depts → org-wide chip, or a retired
                            caption when the row is DEPARTMENT-scoped but all its
                            tags are dead (RC-4). Dark org → nothing. */}
                        {(p.department_ids || []).length > 0 ? (
                          <>
                            <span className="pcp-depts-caption">Visible to:</span>
                            {(p.department_ids || []).map((did) => (
                              <span
                                key={did}
                                className="pcp-chip"
                                style={{
                                  background: isDark ? '#3730a3' : '#e0e7ff',
                                  color: isDark ? '#e0e7ff' : '#3730a3',
                                  borderColor: isDark ? '#4f46e5' : '#a5b4fc',
                                }}
                              >
                                🏷 {deptNameById[did] || did}
                              </span>
                            ))}
                          </>
                        ) : activeDepts.length > 0 ? (
                          p.owner_scope === 'DEPARTMENT' ? (
                            <span className="pcp-depts-caption">Org-wide (tagged departments retired)</span>
                          ) : (
                            <span className="pcp-chip">Org-wide</span>
                          )
                        ) : null}
                        {p.active === false && (
                          <span className="pcp-chip pcp-chip-inactive">Deactivated</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="pcp-actions">
                        <button
                          className="pcp-btn pcp-btn-link"
                          onClick={() => openEdit(p)}
                          disabled={!!form}
                        >
                          Edit
                        </button>
                        {p.active !== false && (
                          <button
                            className="pcp-btn pcp-btn-link pcp-btn-danger-link"
                            onClick={() => askDeactivate(p)}
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {/* B6 / S4 — Variant-A inline edit expands directly below its row */}
                  {isEditing && (
                    <tr className="pcp-edit-row">
                      <td colSpan={6}>{formPanel}</td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </main>

      {confirm && (
        <ConfirmationModal
          isOpen={true}
          title={confirm.title}
          message={confirm.message}
          ctaLabel="Deactivate"
          cancelLabel="Cancel"
          variant="danger"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}
